<template>
  <v-dialog :value="value" max-width="520px" @input="close">
    <v-card class="pa-6 pa-sm-8">
      <v-btn class="button-close ma-4" icon @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <h2 class="mb-6">{{ isAccrue ? 'Начислить тесты' : 'Распределить из буфера' }}</h2>

      <v-autocomplete
        v-model="form.test"
        :items="testOptions"
        :loading="loadingTests"
        label="Тест *"
        item-text="name"
        item-value="code"
        outlined dense
        :error-messages="fieldErrors.test"
        no-data-text="Тесты не найдены"
      />

      <v-autocomplete
        v-if="!isAccrue"
        v-model="form.manager"
        :items="managers"
        label="Менеджер *"
        item-text="name"
        item-value="id"
        outlined dense
        :error-messages="fieldErrors.manager"
        no-data-text="У компании нет менеджеров"
      />

      <v-text-field
        v-model.number="form.amount"
        label="Количество *"
        type="number"
        min="1"
        outlined dense
        :hint="amountHint"
        persistent-hint
        :error-messages="fieldErrors.amount"
      />

      <v-alert v-if="serverErrors.length" type="error" dense outlined class="mt-4 mb-0">
        <div v-for="(err, ind) in serverErrors" :key="ind">{{ err }}</div>
      </v-alert>

      <div class="d-flex justify-end mt-6" style="gap: 12px">
        <v-btn text @click="close">Отмена</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="saving" @click="submit">
          {{ isAccrue ? 'Начислить' : 'Распределить' }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/common/api';

// Начисление админом в буфер корпа (П-3) и распределение корпом из буфера менеджеру (П-5).
// Ошибки сервера показываем в диалоге, не закрывая его (Э-6).
export default {
  name: 'WalletTransferDialog',
  props: {
    value: { type: Boolean, default: false },
    // accrue — начислить в буфер корпа, distribute — распределить из буфера менеджеру
    mode: { type: String, required: true, validator: (v) => ['accrue', 'distribute'].includes(v) },
    corpId: { type: [Number, String], required: true },
    // Тесты из сводки кошелька (модель адаптера) — для распределения и подсказки остатка в буфере
    items: { type: Array, default: () => [] },
    managers: { type: Array, default: () => [] },
    presetTest: { type: String, default: null },
    presetManager: { type: [Number, String], default: null },
  },
  data: () => ({
    form: { test: null, manager: null, amount: null },
    fieldErrors: {},
    serverErrors: [],
    saving: false,
    nomenclatures: [],
    loadingTests: false,
  }),
  computed: {
    isAccrue() {
      return this.mode === 'accrue';
    },
    testOptions() {
      if (!this.isAccrue) {
        // Распределить можно только то, что лежит в буфере
        return this.items.filter((i) => i.buffer.balance > 0).map((i) => ({ code: i.code, name: i.name }));
      }
      // Начислить — любой тест кошелька: сводка отдаёт все активные услуги, даже с нулями
      if (this.items.length) return this.items.map((i) => ({ code: i.code, name: i.name }));
      return this.nomenclatures;
    },
    bufferOfTest() {
      const item = this.items.find((i) => i.code === this.form.test);
      return item ? item.buffer.balance : 0;
    },
    amountHint() {
      if (this.isAccrue || !this.form.test) return '';
      return `В буфере: ${this.bufferOfTest}`;
    },
  },
  watch: {
    value(val) {
      if (!val) return;
      this.form = { test: this.presetTest, manager: this.presetManager, amount: null };
      this.fieldErrors = {};
      this.serverErrors = [];
      if (this.isAccrue && !this.items.length && !this.nomenclatures.length) this.loadNomenclatures();
    },
  },
  methods: {
    async loadNomenclatures() {
      this.loadingTests = true;
      try {
        const list = await api.get('/stock/nomenclatures/');
        this.nomenclatures = (Array.isArray(list) ? list : (list.results || []))
          .filter((n) => n.is_active !== false && n.is_licensed !== false)
          .map((n) => ({ code: n.code, name: n.name || n.code }));
      } catch (e) {
        this.serverErrors = ['Не удалось загрузить список тестов'];
      } finally {
        this.loadingTests = false;
      }
    },
    validate() {
      const errors = {};
      const amount = Number(this.form.amount);
      if (!this.form.test) errors.test = ['Выберите тест'];
      if (!this.isAccrue && !this.form.manager) errors.manager = ['Выберите менеджера'];
      if (!Number.isInteger(amount) || amount < 1) {
        errors.amount = ['Целое число больше нуля'];
      } else if (!this.isAccrue && amount > this.bufferOfTest) {
        errors.amount = [`В буфере только ${this.bufferOfTest}`];
      }
      this.fieldErrors = errors;
      return !Object.keys(errors).length;
    },
    async submit() {
      if (this.saving || !this.validate()) return;
      this.saving = true;
      this.serverErrors = [];
      const url = this.$store.getters['users/counters/getEndpoint'](this.isAccrue ? 'corp_client' : 'manager');
      if (!url) {
        this.serverErrors = ['Операция недоступна для вашей роли'];
        this.saving = false;
        return;
      }
      const body = { test: this.form.test, amount: Number(this.form.amount) };
      if (this.isAccrue) body.corp_client = Number(this.corpId);
      else body.manager = Number(this.form.manager);
      try {
        await api.post(url, body);
        this.$toast.success(this.isAccrue ? 'Тесты начислены' : 'Тесты распределены', '', { position: 'topRight' });
        this.$emit('success');
        this.$emit('input', false);
      } catch (err) {
        const data = (err && err.data) || {};
        const messages = [];
        Object.entries(data).forEach(([key, val]) => {
          const list = Array.isArray(val) ? val : [val];
          if (['test', 'manager', 'amount'].includes(key)) this.$set(this.fieldErrors, key, list.map(String));
          else list.forEach((m) => messages.push(typeof m === 'string' ? m : JSON.stringify(m)));
        });
        if (!messages.length && !Object.keys(this.fieldErrors).length) messages.push('Не удалось выполнить операцию');
        this.serverErrors = messages;
      } finally {
        this.saving = false;
      }
    },
    close() {
      if (!this.saving) this.$emit('input', false);
    },
  },
};
</script>
